import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Wrench, Users, Phone, MapPin, ScanLine } from 'lucide-react';
import { Button } from '../components/ui/button';
import LutromLogo from '../components/LutromLogo';
import { QrScanner } from '../components/QrScanner';
import { toast } from 'sonner';

const LandingPage = () => {
  const [scanOpen, setScanOpen] = useState(false);
  const navigate = useNavigate();

  const handleScan = (decoded) => {
    setScanOpen(false);
    try {
      const url = new URL(decoded);
      navigate(url.pathname + url.search);
    } catch {
      if (decoded.startsWith('/')) navigate(decoded);
      else toast.error('Unrecognised QR code');
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Top bar */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <LutromLogo size="md" />
          <div className="flex items-center gap-3">
            <a href="tel:+447599474730" className="hidden sm:flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900">
              <Phone className="h-4 w-4" /> +44 7599 474730
            </a>
            <Link to="/login">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white">
                Sign In <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative flex-1 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src="https://images.unsplash.com/photo-1496247749665-49cf5b1022e9" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-900/95 to-slate-900/60" />

        <div className="relative max-w-6xl mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.25em] text-blue-400 uppercase mb-6">
              <span className="h-px w-8 bg-blue-700" /> Internal Portal
            </div>
            <h1 className="font-display uppercase text-5xl md:text-6xl leading-[1.05] mb-6">
              Lutrom Engineering<br />
              <span className="lutrom-metal-text">Operations Portal</span>
            </h1>
            <p className="text-lg text-slate-300 max-w-xl leading-relaxed mb-8">
              Secure access for Lutrom employees and our customers. Track work orders, log time, view reports, and submit work requests &mdash; all in one place.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link to="/login">
                <Button size="lg" className="lutrom-metal-btn font-semibold px-8 h-12 border-0">
                  Sign In to Portal <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href="tel:+447599474730">
                <Button size="lg" variant="outline" className="border-slate-400 text-white hover:bg-white/10 h-12 bg-transparent">
                  <Phone className="mr-2 h-4 w-4" /> 24/7 Call Out
                </Button>
              </a>
            </div>
            <div className="mt-10 text-sm text-slate-400">
              Need access? Speak to your Lutrom contact &mdash; accounts are provisioned by our team.
            </div>
          </div>

          {/* Right: QR scan card + role tags */}
          <div className="grid gap-4">
            <div className="lutrom-metal rounded-md p-6 shadow-2xl">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-12 w-12 bg-slate-950 rounded flex items-center justify-center">
                  <ScanLine className="h-6 w-6 text-blue-400" />
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-200">Operators</div>
                  <div className="font-display uppercase text-xl text-white">Report an issue</div>
                </div>
              </div>
              <p className="text-sm mb-4 leading-relaxed text-blue-50">
                Scan the QR code on any Lutrom-tagged asset to instantly submit a work request &mdash; no login needed.
              </p>
              <Button
                onClick={() => setScanOpen(true)}
                size="lg"
                className="w-full bg-slate-950 hover:bg-slate-900 text-blue-300 font-semibold h-12 border border-slate-800"
              >
                <ScanLine className="mr-2 h-5 w-5" /> Scan QR Code
              </Button>
            </div>

            {[
              { icon: ShieldCheck, title: 'Lutrom Admin', desc: 'Operations management & scheduling.' },
              { icon: Wrench, title: 'Lutrom Engineers', desc: 'Time clock, jobs, schedule & chat.' },
              { icon: Users, title: 'Lutrom Customers', desc: 'Requests, reports & invoices.' },
            ].map((r, i) => (
              <div key={i} className="bg-slate-800/60 backdrop-blur border border-slate-700 rounded-md p-4 flex items-center gap-3 hover:border-blue-700/60 transition-colors">
                <div className="h-9 w-9 bg-slate-900 rounded flex items-center justify-center shrink-0 border border-slate-700">
                  <r.icon className="h-4 w-4 text-blue-400" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-white text-sm">{r.title}</div>
                  <div className="text-xs text-slate-400">{r.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <QrScanner open={scanOpen} onClose={() => setScanOpen(false)} onScan={handleScan} />

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-8 border-t border-slate-800">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-6 items-center">
          <div className="flex items-center gap-3">
            <LutromLogo dark size="sm" />
          </div>
          <div className="flex items-center gap-2 text-sm justify-center">
            <MapPin className="h-4 w-4 text-blue-700" /> North West, England
          </div>
          <div className="flex items-center gap-4 text-sm md:justify-end">
            <a href="tel:+447599474730" className="flex items-center gap-2 hover:text-white"><Phone className="h-4 w-4" /> +44 7599 474730</a>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 mt-6 pt-6 border-t border-slate-800 text-xs text-slate-500 flex justify-between flex-wrap gap-3">
          <div>&copy; {new Date().getFullYear()} Lutrom Engineering Ltd. All rights reserved.</div>
          <div className="flex items-center gap-4 flex-wrap">
            <a
              href="https://www.lutrom.co.uk/terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white"
              data-testid="landing-terms-link"
            >
              Terms of Use
            </a>
            <a
              href="https://www.lutrom.co.uk/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white"
              data-testid="landing-privacy-link"
            >
              Privacy Policy
            </a>
            <a
              href="https://www.lutrom.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white"
            >
              www.lutrom.co.uk
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
